import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { keys } from '../../api/keys'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { Panel } from '../../platform/primitives/Panel'
import {
  fetchPendingIds,
  refreshTime,
  ReviewConflictError,
  useMarkReviewed,
  useReviewSummary,
  type ReviewSummary,
} from './useReview'
import './review.css'

const n = (count: number) => count.toLocaleString('en-CA')

/** "12 new bank transactions fetched in the last refresh — 9 auto-tagged!". The tag clause is dropped at zero. */
export function reviewCardCopy(s: ReviewSummary): string {
  const base = `${n(s.pendingCount)} new bank transaction${s.pendingCount === 1 ? '' : 's'} fetched in the last refresh`
  return s.autoTaggedCount > 0 ? `${base} — ${n(s.autoTaggedCount)} auto-tagged!` : `${base}.`
}

export const CONFLICT_MESSAGE = 'Some of these changed since the list loaded. Refreshed — try again.'

/**
 * The entry card on `/transactions` (M6-R3, Q2), above the grid. Shown while the inbox has pending
 * rows. **Review** is the card's primary action, so it is an outline button and the card is a plain
 * panel: lime is not also spent on a hero tile (rule 9). Dismiss records `dismissed` for the pending
 * ids, which is not the same as having reviewed them.
 *
 * It reads `GET /transactions/review/summary`, not the list's per-month count (Q1): the two can
 * differ, since this one is not date-scoped. A loading or failed summary renders nothing: the card
 * is an offer, and an absent offer is not an error on the list beneath it.
 */
export function ReviewCard() {
  const summary = useReviewSummary()
  const navigate = useNavigate()
  const { run } = useMarkReviewed()
  const client = useQueryClient()
  /** The ids the dialog is about to send: read when it opens, so its title and the request are one list. */
  const [plan, setPlan] = useState<{ ids: string[]; truncated: boolean } | null>(null)
  const [looking, setLooking] = useState(false)
  const [lookFailed, setLookFailed] = useState(false)
  if (summary.status !== 'success' || summary.data.pendingCount === 0) return null
  const s = summary.data

  const ask = async () => {
    if (looking) return
    setLooking(true)
    setLookFailed(false)
    try {
      const found = await fetchPendingIds()
      // Everything was reviewed elsewhere since the summary: nothing to dismiss, so refresh the card away.
      if (found.ids.length === 0) void client.invalidateQueries({ queryKey: keys.review.all() })
      else setPlan(found)
    } catch {
      setLookFailed(true)
    } finally {
      setLooking(false)
    }
  }

  return (
    <Panel className="review-card">
      <div className="review-card__text">
        <p className="review-card__title">{reviewCardCopy(s)}</p>
        {s.refreshedAt !== null && <p className="review-card__meta">Fetched at {refreshTime(s.refreshedAt)}</p>}
      </div>
      <div className="review-card__actions">
        <Button type="button" size="page" variant="outline" onClick={() => navigate('/review')}>
          Review
        </Button>
        <Button type="button" size="page" variant="outline" aria-disabled={looking} onClick={() => void ask()}>
          Dismiss
        </Button>
      </div>
      {lookFailed && (
        <p className="review-card__meta" role="alert">
          Couldn’t load the transactions to dismiss. Try again in a moment.
        </p>
      )}
      <ConfirmDialog
        open={plan !== null}
        mode="dismiss_review"
        title={
          plan === null
            ? ''
            : plan.truncated
              ? `Dismiss the first ${n(plan.ids.length)} transactions from review? The rest stay in review.`
              : `Dismiss ${n(plan.ids.length)} transaction${plan.ids.length === 1 ? '' : 's'} from review?`
        }
        body="They stay in your list; they just won’t be flagged."
        confirmLabel="Dismiss"
        pendingLabel="Dismissing…"
        onCancel={() => setPlan(null)}
        onConfirm={async () => {
          if (plan === null) return
          await run(plan.ids, 'dismissed')
          setPlan(null)
        }}
        errorFor={(e) =>
          e instanceof ReviewConflictError ? CONFLICT_MESSAGE : 'Couldn’t dismiss. Nothing was changed. Try again in a moment.'
        }
      />
    </Panel>
  )
}
